import axios from 'axios';
import { camelCase } from 'change-case';
import { sendEvent, POST_EVENTS, ExtendElementType } from './events';
import { AirwallexEnv, Intent, BoxStyle } from '@airwallex/airwallex-payment-elements';
import { detect } from 'detect-browser';
import { deviceOSMapping } from '~components/constant';

export const getOS = () => detect()?.os;
export const isHPP = () => window.location.href.indexOf('standalone') !== -1;
export const isMobileDevice = () => {
  const os = getOS();
  return os && ['android', 'ios'].includes(deviceOSMapping[os]);
};

export const styleString = (style: BoxStyle['base']) =>
  style &&
  Object.entries(style)
    .map(([k, v]) => `${k}:${v}`)
    .join(';');

export const clientStoreItem = (key: string, value: string) => localStorage.setItem(key, value);
export const clientRetrieveItem = (key: string) => localStorage.getItem(key) || '';
export const clientRemoveItem = (key: string) => localStorage.removeItem(key);
export function getKeyByValue(object: { [key: string]: unknown }, value: unknown) {
  return Object.keys(object).find((key) => object[key] === value);
}

export const simulateFormSubmit = (data: { [k: string]: string }, action: string) => {
  const formEle = document.createElement('form');
  formEle.setAttribute('method', 'post');
  formEle.setAttribute('style', 'height: 0; overflow: hidden;');
  formEle.action = action;
  Object.keys(data).forEach((key: string) => {
    const inputEle = document.createElement('input');
    inputEle.type = 'hidden';
    inputEle.name = key;
    inputEle.value = data[key];
    formEle.appendChild(inputEle);
  });
  const submitBtn = document.createElement('input');
  submitBtn.type = 'submit';
  formEle.appendChild(submitBtn);
  document.body.appendChild(formEle);
  submitBtn.click();
};

export const getLang = () => {
  const lang = clientRetrieveItem('awx_lang');
  return ['zh', 'en'].includes(lang) ? lang : 'en';
};

export const getAirwallexEnv = (): AirwallexEnv | undefined => {
  const hostName = window.location.hostname;
  if (hostName.indexOf('dev') !== -1) {
    return 'dev';
  } else if (hostName.indexOf('preview') !== -1) {
    return 'preview';
  } else if (hostName.indexOf('qa') !== -1) {
    return 'qa';
  } else if (hostName.indexOf('staging') !== -1) {
    return 'staging';
  } else if (hostName.indexOf('demo') !== -1) {
    return 'demo';
  } else if (hostName === 'checkout.airwallex.com' || hostName === 'api-checkout.airwallex.com') {
    return 'prod';
  } else {
    return 'dev';
  }
};

export const getAirwayUrl = () => {
  const airwallexEnv = getAirwallexEnv();
  if (window.location.hostname.includes('local')) {
    // Local development
    return `${window.location.origin}/api/v1/checkout`;
  } else if (airwallexEnv === 'prod') {
    // Prod deployment
    return 'https://pci-api.airwallex.com/api/v1/pa';
  }
  // Staging, demo deployment
  return `https://pci-api-${airwallexEnv}.airwallex.com/api/v1/pa`;
};

export const getCheckoutApiUrl = () => {
  const airwallexEnv = getAirwallexEnv();
  if (window.location.hostname.includes('local')) {
    // Local development
    return `${window.location.origin}/api/v1/checkout`;
  } else if (airwallexEnv === 'prod') {
    // Prod deployment
    return 'https://pci-api.airwallex.com/api/v1/checkout';
  }
  // Staging, demo deployment
  return `https://pci-api-${airwallexEnv}.airwallex.com/api/v1/checkout`;
};

export const encode = async (value: string, client_secret: string): Promise<string> => {
  try {
    const res = await axios.post(
      `${getCheckoutApiUrl()}/encode`,
      { value },
      {
        withCredentials: false,
        headers: {
          'client-secret': client_secret,
          'Content-Type': 'application/json',
          'Awx-Tracker': clientRetrieveItem('awx_tracker'),
        },
      },
    );
    return res.data;
  } catch {
    return value;
  }
};

export const decode = async (value: string, client_secret: string): Promise<string> => {
  try {
    const res = await axios.post(
      `${getCheckoutApiUrl()}/decode`,
      { value },
      {
        withCredentials: false,
        headers: {
          'client-secret': client_secret,
          'Content-Type': 'application/json',
          'Awx-Tracker': clientRetrieveItem('awx_tracker'),
        },
      },
    );
    return res.data;
  } catch {
    return value;
  }
};

export const convertKeyToCamelCase = (data: { [key: string]: string }) => {
  const newData: { [key: string]: string } = {};
  Object.keys(data).forEach((key) => {
    newData[camelCase(key)] = data[key];
  });

  return newData;
};

let fraudSessionId = '';

export const getSessionId = (id: string): string => {
  if (!fraudSessionId) {
    const airwallexEnv = getAirwallexEnv();
    console.log('airwallexEnv', airwallexEnv);
    const ORG_ID = airwallexEnv === 'prod' ? '9ozphlqx' : '45ssiuz3';
    const script = document.createElement('script');
    fraudSessionId = `${id}${Date.now()}`;
    script.src = `https://h.online-metrix.net/fp/tags.js?org_id=${ORG_ID}&session_id=${fraudSessionId}`;
    document.body.appendChild(script);
  }

  return fraudSessionId;
};

interface SendRequestLogRequest {
  componentName: ExtendElementType;
  reqPath: string;
  reqHeader: object;
  reqPayload?: object;
  origin: string;
}

export const sendRequestLog = ({ componentName, reqPath, reqHeader, reqPayload, origin }: SendRequestLogRequest) => {
  sendEvent(
    {
      type: componentName,
      code: POST_EVENTS.onLogRequest,
      path: reqPath,
      req: {
        ...reqPayload,
        request_id: null,
      },
      header: {
        ...reqHeader,
        'Awx-Tracker': null,
      },
    },
    origin,
  );
};

export const buildCardConsentsFromIntent = (intent: Intent) =>
  (intent.customer_payment_consents ?? [])
    .filter(
      (consent) =>
        consent?.payment_method?.type === 'card' &&
        consent?.status === 'VERIFIED' &&
        consent?.next_triggered_by === 'customer',
    )
    .map((consent) => {
      const hintPaymentMethod = (intent.customer_payment_methods ?? []).find(
        (paymentMethod) => paymentMethod.id === consent.payment_method?.id,
      );
      const { id, requires_cvc } = consent;
      const { bin, brand, last4 } = hintPaymentMethod?.card ?? {};
      return {
        id,
        requires_cvc,
        brand,
        bin,
        last4,
      };
    });
